import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Collapse, Typography, useTheme, useMediaQuery } from "@material-ui/core";

import { getUserFinancials, getUserFinancialsReceived, getUserFinancialsRecommender } from "../../../../actions";
import InfoList, { InfoListItem } from "../../../../custom/InfoList";
import NumberFmt from "../../../../custom/Number";
import UserInfoSummary from "./UserInfoSummary";
import InfoCollapseSection from "./InfoCollapseSection";
import ContractsTable from "./ContractsTable";
import { ContractPhases, Roles } from "../../../../util";

const aptColor = { true: "green", false: "red" };

const UserContractDetails = ({ currentUser, user, open }) => {
  const isAgent = useMemo(() => user.role === Roles.Agent, [user.role]);
  const [details, setDetails] = useState([]);
  const [receivedValues, setReceivedValues] = useState();
  const [recommenderValues, setRecommenderValues] = useState();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchData = async () => {
      const [details, receivedValues, recommenderValues] = await Promise.all([
        getUserFinancials(user.id),
        isAgent ? getUserFinancialsReceived(user.id) : Promise.resolve(null),
        getUserFinancialsRecommender(user.id),
      ]);
      setDetails(details);
      if (isAgent) {
        setReceivedValues(receivedValues);
      }
      setRecommenderValues(recommenderValues);
    };

    fetchData();
  }, [user, isAgent]);

  const receivedItems = useMemo(
    () => [
      ...(isAgent
        ? [
            { id: "BONUS", value: receivedValues?.commissionReceived },
            { id: "BONUS_MANAGER", value: receivedValues?.managementFeeReceived },
            { id: "BONUS_LEADERSHIP_ONE_TIME", value: receivedValues?.leadershipOneTimeCommissionReceived },
            { id: "BONUS_MANAGER_SPLIT", value: receivedValues?.managerSplitReceived },
            { id: "BONUS_SPLIT", value: receivedValues?.splitReceived },
          ]
        : []),
      { id: "BONUS_RECOMMENDANT", value: recommenderValues?.totalReceived },
    ],
    [isAgent, receivedValues, recommenderValues],
  );

  const generatedItems = useMemo(
    () => [
      { id: "BONUS", value: details?.commissionGenerated },
      { id: "BONUS_MANAGER", value: details?.managementFeeGenerated },
      { id: "BONUS_LEADERSHIP_ONE_TIME", value: details?.leaderOneTimeCommissionGenerated },
      { id: "BONUS_MANAGER_SPLIT", value: details?.managerSplitGenerated },
      { id: "BONUS_SPLIT", value: details?.splitGenerated },
      { id: "BONUS_RECOMMENDANT", value: details?.recommenderGenerated },
    ],
    [details],
  );

  const hasExecutingContracts = useMemo(() => details?.contracts?.some((contract) => contract.phase === ContractPhases.Executing), [details]);

  return (
    <>
      <Collapse
        in={open}
        timeout="auto"
        mountOnEnter
        unmountOnExit
      >
        <div
          style={{
            maxWidth: isMobile ? "100%" : "55%",
            paddingLeft: 75,
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: 16,
            justifyContent: "space-between",
          }}
        >
          <UserInfoSummary
            user={user}
            currentUser={currentUser}
            isAgent={isAgent}
            aptColor={aptColor}
          />
          <InfoList divider>
            <Typography
              color="textPrimary"
              gutterBottom
              variant="body1"
            >
              <FormattedMessage id="app.managerAndAgents.contracts.yield" />
            </Typography>
            {hasExecutingContracts ? (
              <InfoListItem
                label={<FormattedMessage id="app.managerAndAgents.incomeReceived" />}
                info={
                  <NumberFmt
                    value={details?.yieldReceived}
                    currency="LCT"
                    color={theme.palette.text.primary}
                  />
                }
              />
            ) : (
              <Typography
                gutterBottom
                color="textSecondary"
              >
                <FormattedMessage id="app.managerAndAgents.contracts.noActive" />
              </Typography>
            )}
          </InfoList>
          <InfoList>
            <InfoCollapseSection
              enableCollapse={isAgent}
              title={<FormattedMessage id="app.commission.received" />}
              total={receivedValues?.totalReceived}
              showTotal={isAgent}
            >
              {receivedItems.map((r) => (
                <InfoListItem
                  key={r.id}
                  label={<FormattedMessage id={`app.commission.type.${r.id}`} />}
                  info={
                    <NumberFmt
                      value={r.value}
                      currency="LCT"
                    />
                  }
                  infoTextColor={isAgent ? "textSecondary" : "textPrimary"}
                />
              ))}
            </InfoCollapseSection>
          </InfoList>
          <InfoList>
            <InfoCollapseSection
              enableCollapse={hasExecutingContracts}
              title={<FormattedMessage id="app.commission.generated" />}
              total={details?.totalCommissionGenerated}
              showTotal={hasExecutingContracts}
            >
              {hasExecutingContracts ? (
                <>
                  {generatedItems.map((item) => (
                    <InfoListItem
                      key={item.id}
                      label={<FormattedMessage id={`app.commission.type.${item.id}`} />}
                      info={
                        <NumberFmt
                          value={item.value}
                          currency="LCT"
                        />
                      }
                    />
                  ))}
                </>
              ) : (
                <Typography
                  gutterBottom
                  color="textSecondary"
                >
                  <FormattedMessage id="app.managerAndAgents.contracts.noActive" />
                </Typography>
              )}
            </InfoCollapseSection>
          </InfoList>
        </div>

        <ContractsTable contracts={details?.contracts} />
      </Collapse>
    </>
  );
};

export default UserContractDetails;
